import { Sheet, Tabs, ScrollArea, lucideIcons, Skeleton, Image, Separator } from '@round/creatorbase-ui';
import styles from './CreatorDetailsModal.module.css';
import { useCallback, useEffect, useState, ReactNode, UIEvent } from 'react';
import cn from 'classnames';
import { isNumber } from 'utility/utility';
import useCreatorData from './useCreatorData';
import Accounts from './Accounts/Accounts';
import ClientInfluencerForm from './ClientInfluencerForm/ClientInfluencerForm';
import Stats from './Stats/Stats';
import Audience from './Tabs/Audience/Audience';
import Engagement from './Tabs/Engagement/Engagement';
import PreviousCampaigns from './Tabs/PreviousCampaigns/PreviousCampaigns';
import Lookalikes from './Tabs/Lookalikes/Lookalikes';
import PoolsRow from './PoolsRow/PoolsRow';
import AddToPoolButton from 'Modules/Plans/CreatorPools/components/AddToPoolButton/AddToPoolButton';
import { useAccountPools } from './useAccountPools';
import useUserAnalytics from './useUserAnalytics';
import { creatorbase } from '@round/api';
import PlatformSelect from './PlatformSelect/PlatformSelect';
import CompactHeader from './CompactHeader/CompactHeader';
import AddCreatorToCampaignsDropdown from '../AddCreatorToCampaignsDropdown/AddCreatorToCampaignsDropdown';
import AccountLinkButton from './AccountLinkButton';
import { useClientInfluencer, useClientInfluencerAccount } from '@round/creatorbase-helpers/api-hooks';

const { BarChart3, Play, Users } = lucideIcons;

const SCROLL_COMPACT_THRESHOLD_PX = 60;

type Props = {
    isOpen: boolean;
    onClose: (hasUpdated: boolean) => void;
    clientInfluencerAccountId: number | undefined;
    contextTools?: (clientInfluencerAccount: creatorbase.ClientInfluencerAccount | undefined) => ReactNode;
};

const CreatorDetailsModal = ({ isOpen, onClose, clientInfluencerAccountId, contextTools }: Props) => {
    // client influencer account represents a platform account (e.g. Instagram, TikTok account)
    // ClientInfluencerAccount.client_influencer_id - is a unique id of a creator used to identify
    // platform accounts that belong to them.
    const [activeClientInfluencerAccountId, setActiveClientInfluencerAccountId] = useState(clientInfluencerAccountId);
    const { clientInfluencer: clientInfluencerAccount, isLoading: isClientInfluencerAccountLoading } =
        useClientInfluencerAccount(activeClientInfluencerAccountId, {
            enabled: isOpen && isNumber(activeClientInfluencerAccountId),
        });

    // keeps the same value until the clientInfluencer changes
    // avoids unnecessary fetches of creator data when clientInfluencer is undefined (loading)
    const clientInfluencerId = useMemoizedClientInfluencerId(clientInfluencerAccount?.client_influencer_id);
    const { data: clientInfluencer } = useClientInfluencer(clientInfluencerId, {
        enabled: isOpen && !!clientInfluencerId,
    });
    const { accounts, getAccount, getProfileImage, isAccountsLoading, isAccountsPending } = useCreatorData(
        clientInfluencerId,
        {
            enabled: isOpen && !!clientInfluencerId,
        }
    );

    const {
        account: activeAccount,
        stats: activeAccountStats,
        isLoading: isActiveAccountLoading,
    } = getAccount(activeClientInfluencerAccountId);
    const profileImage = getProfileImage(activeAccount);
    const availablePlatforms = accounts.map((account) => account.platform);
    const accountPools = useAccountPools(activeClientInfluencerAccountId, { enabled: isOpen });
    // While memberships load the row holds the space with skeletons, so the toolbar button
    // doesn't flash in before the chips arrive. A failed load keeps the row too: offering
    // "add to a pool" would assert this account is in none, which we don't know.
    const shouldShowPoolsRow = accountPools.isLoading || accountPools.isError || accountPools.pools.length > 0;

    // Audience and Lookalikes both derive from the user analytics query. When it 404s
    // (no analytics for this creator), the query errors and no data is returned - in that
    // case we hide both tabs rather than showing empty/error states.
    const { data: userAnalytics } = useUserAnalytics(activeAccount, {
        enabled: isOpen && !!activeAccount && !isActiveAccountLoading,
    });
    const hasAnalyticsData = !!userAnalytics;
    const [activeTab, setActiveTab] = useState('engagement');
    const [hasUpdated, setHasUpdated] = useState(false);

    const [isScrolled, setIsScrolled] = useState(false);
    const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
        setIsScrolled(event.currentTarget.scrollTop > SCROLL_COMPACT_THRESHOLD_PX);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setIsScrolled(false);
            setHasUpdated(false);
            setActiveTab('engagement');
        }
    }, [isOpen]);

    return (
        <>
            <Sheet
                open={isOpen}
                onOpenChange={(_, event) => {
                    if (
                        event.reason === 'outside-press' &&
                        !(event.event.currentTarget as HTMLElement).attributes
                            .getNamedItem('data-slot')
                            ?.value.includes('overlay')
                    ) {
                        return;
                    }
                    onClose(hasUpdated);
                }}
            >
                <Sheet.Content side="bottom" className={styles.modal}>
                    <ScrollArea className={styles.scrollArea} onScroll={handleScroll}>
                        <CompactHeader
                            account={activeAccount}
                            clientInfluencer={clientInfluencer}
                            image={profileImage?.imageUrl ?? undefined}
                            isImageLoading={Boolean(
                                profileImage?.isLoading || isActiveAccountLoading || isAccountsPending
                            )}
                            accounts={accounts}
                            activeAccountId={activeAccount?.creatorbase_client_influencer_account_id}
                            onSelectAccount={setActiveClientInfluencerAccountId}
                            isAccountsLoading={isAccountsLoading || isAccountsPending}
                            toolbar={contextTools?.(clientInfluencerAccount)}
                            className={cn(styles.compactHeader, { [styles.compactHeaderConcealed]: !isScrolled })}
                        />

                        <Sheet.Header className={styles.header}>
                            <Image
                                src={profileImage?.imageUrl ?? ''}
                                alt={activeAccount?.full_name}
                                isLoading={profileImage?.isLoading || isActiveAccountLoading || isAccountsPending}
                                className={styles.profileImage}
                            />

                            <div className={styles.headerContent}>
                                <Sheet.Title className={styles.title}>
                                    {isActiveAccountLoading ? (
                                        <Skeleton className={styles.titleSkeleton} />
                                    ) : (
                                        activeAccount?.full_name
                                    )}
                                </Sheet.Title>

                                <div className={styles.accountsContainer}>
                                    <Accounts
                                        accounts={accounts}
                                        activeAccountId={activeAccount?.creatorbase_client_influencer_account_id}
                                        onSelectAccount={setActiveClientInfluencerAccountId}
                                        isLoading={isAccountsLoading}
                                    />

                                    <AccountLinkButton
                                        accountCount={accounts.length}
                                        activeAccount={activeAccount}
                                        clientInfluencerId={clientInfluencerId}
                                        className={styles.linkButton}
                                    />

                                    <div className={styles.toolbar}>
                                        {!shouldShowPoolsRow && (
                                            <>
                                                <AddToPoolButton
                                                    disabled={!activeAccount}
                                                    onAdd={accountPools.addPool}
                                                />

                                                {contextTools && (
                                                    <Separator
                                                        orientation="vertical"
                                                        className={styles.toolbarSeparator}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {contextTools ? (
                                            contextTools(clientInfluencerAccount)
                                        ) : (
                                            <AddCreatorToCampaignsDropdown
                                                accountId={activeAccount?.creatorbase_client_influencer_account_id}
                                                platform={activeAccount?.platform}
                                                clientInfluencer={clientInfluencer}
                                            />
                                        )}
                                    </div>
                                </div>

                                {shouldShowPoolsRow && (
                                    <PoolsRow
                                        pools={accountPools.pools}
                                        isLoading={accountPools.isLoading}
                                        onAdd={accountPools.addPool}
                                        onRemove={accountPools.removePool}
                                        className={styles.poolsRow}
                                    />
                                )}

                                <ClientInfluencerForm
                                    isLoading={isClientInfluencerAccountLoading}
                                    clientInfluencerId={clientInfluencerId}
                                    className={styles.clientInfluencerAccount}
                                    onUpdate={() => setHasUpdated(true)}
                                />
                            </div>
                        </Sheet.Header>

                        <Tabs
                            orientation="horizontal"
                            value={activeTab}
                            onValueChange={setActiveTab}
                            className={styles.tabs}
                        >
                            <div className={styles.stickyStatsSection}>
                                <div className={styles.statsContainer}>
                                    <PlatformSelect
                                        availablePlatforms={availablePlatforms}
                                        isLoading={isAccountsLoading}
                                        value={activeAccount?.platform}
                                        onValueChange={(platform) => {
                                            const account = accounts.find((account) => account.platform === platform);
                                            if (account) {
                                                setActiveClientInfluencerAccountId(
                                                    account.creatorbase_client_influencer_account_id
                                                );
                                            }
                                        }}
                                    />

                                    <Stats
                                        className={cn(styles.stats, { [styles.statsConcealed]: isScrolled })}
                                        stats={activeAccountStats}
                                        isLoading={isActiveAccountLoading}
                                    />
                                </div>

                                <Tabs.List variant="line" className={styles.tabList}>
                                    {hasAnalyticsData && (
                                        <Tabs.Trigger value="audience" className={styles.tabListTrigger}>
                                            <Users className={styles.tabListTriggerIcon} />
                                            Audience
                                        </Tabs.Trigger>
                                    )}
                                    <Tabs.Trigger value="engagement" className={styles.tabListTrigger}>
                                        <BarChart3 className={styles.tabListTriggerIcon} />
                                        Engagement
                                    </Tabs.Trigger>
                                    <Tabs.Trigger value="previousCampaigns" className={styles.tabListTrigger}>
                                        <Play className={styles.tabListTriggerIcon} />
                                        Previous Campaigns
                                    </Tabs.Trigger>
                                    {hasAnalyticsData && (
                                        <Tabs.Trigger value="lookalikes" className={styles.tabListTrigger}>
                                            <Users className={styles.tabListTriggerIcon} />
                                            Lookalikes
                                        </Tabs.Trigger>
                                    )}
                                </Tabs.List>
                            </div>

                            {hasAnalyticsData && (
                                <Tabs.Content value="audience" className={styles.tabContent}>
                                    <Audience account={activeAccount} isLoading={isActiveAccountLoading} />
                                </Tabs.Content>
                            )}

                            <Tabs.Content value="engagement" className={styles.tabContent} asChild>
                                <Engagement account={activeAccount} isLoading={isActiveAccountLoading} />
                            </Tabs.Content>

                            <Tabs.Content value="previousCampaigns" className={styles.tabContent}>
                                {/* we need influencer id from clientInfluencerAccount to fetch previous campaigns */}
                                <PreviousCampaigns
                                    account={clientInfluencerAccount}
                                    isLoading={isClientInfluencerAccountLoading}
                                />
                            </Tabs.Content>

                            {hasAnalyticsData && (
                                <Tabs.Content value="lookalikes" className={styles.tabContent}>
                                    <Lookalikes account={activeAccount} isLoading={isActiveAccountLoading} />
                                </Tabs.Content>
                            )}
                        </Tabs>
                    </ScrollArea>
                </Sheet.Content>
            </Sheet>
        </>
    );
};

function useMemoizedClientInfluencerId(value: string | undefined) {
    const [clientInfluencerId, setClientInfluencerId] = useState<string | undefined>(value);
    useEffect(() => {
        setClientInfluencerId((prev) => (prev === value || !value ? prev : value));
    }, [value]);

    return clientInfluencerId;
}

export default CreatorDetailsModal;
